<template>
  <v-sheet dark>
    <div :style="styleContainer">
      <div v-if="!isHotspots && !isControlRisksRatings">
        <div class="px-2 hyp-header py-0 mb-3 mt-0">
          <div v-if="!loading && !hasStability">
            <div class="text-center">
              <div class="mx-2 my-3">
                <div class="hyp-no-data-text text-center">
                  No {{ labels.pulse }} score for today {{ location }}
                </div>
              </div>
            </div>
          </div>

          <GridHighlight
            v-if="hasStability && !hasEssentials"
            :type="type"
            :text="score"
            :minScore="minScore"
            :maxScore="maxScore"
            :isHistory="isHistory"
            :scoreDelta="scoreDelta"
            :large-text="true"
            :color="colorPulse"
            @finished="handleChartFinished"
          ></GridHighlight>

          <div class="mt-2" v-if="riskRatings">
            <div class="text-left mb-2 mr-2 pull-left">Risk Ratings</div>
            <PulseAndRiskDefinitions
              class="pull-left ratings-icon-container"
              :initial="'RR'"
            ></PulseAndRiskDefinitions>
            <div class="clearfix"></div>

            <RiskRatingPrimary
              :horizontal="true"
              :risks="riskRatings"
              :use-click="false"
              :dark="true"
              class="mb-5"
            ></RiskRatingPrimary>
          </div>

          <v-btn
            @click="toggleMonitor"
            color="primary"
            class="hyp-open-watch-btn mt-5"
            :style="styleWatchButton"
            :disabled="watchBtnDisabled || hasEssentials"
          >
            <span>Watch Location</span>
          </v-btn>
        </div>
      </div>
      <div v-if="isHotspots && !hasEssentials" class="px-2 hyp-header pb-1">
        <GridHighlight
          :type="type"
          :trigger="triggers"
          :color="hotspotColor"
        ></GridHighlight>
      </div>
      <div v-if="isControlRisksRatings">
        <div class="px-2 hyp-header py-0 mb-3 mt-0">
          <GridHighlight
            v-if="todayScore && !hasEssentials && !hasGrds"
            type="stability"
            :text="score"
            :minScore="minScore"
            :maxScore="maxScore"
            :isHistory="isHistory"
            :scoreDelta="scoreDelta"
            :large-text="true"
            :color="colorPulse"
            @finished="handleChartFinished"
          ></GridHighlight>

          <div>
            <div class="text-left mb-2 mr-2 pull-left">Risk Ratings</div>
            <PulseAndRiskDefinitions
              v-if="!hasGrds"
              class="pull-left ratings-icon-container"
              :initial="'RR'"
            ></PulseAndRiskDefinitions>
            <div class="clearfix"></div>

            <RiskRatingPrimary
              v-if="!hasGrds"
              :horizontal="true"
              :risks="riskRatings"
              :use-click="false"
              :dark="true"
              class="mb-5"
            ></RiskRatingPrimary>

            <RisksPulseRatings
              v-if="hasGrds"
              :selected-location="selectedLocation"
              :selected-primary-risk-category="risksCategory"
              risksContentExpanded=""
              :show-title="false"
              :params="risksParams"
            ></RisksPulseRatings>
          </div>

          <v-btn
            v-if="!hasGrds"
            @click="toggleMonitor"
            color="primary"
            class="mt-5 hyp-open-watch-btn"
            :style="styleWatchButton"
            :disabled="watchBtnDisabled || hasEssentials"
          >
            <span>Watch Location</span>
          </v-btn>
        </div>
      </div>
    </div>
  </v-sheet>
</template>

<script>
import lodashGet from "lodash/get";
import lodashOrderBy from "lodash/orderBy";
import lodashRemove from "lodash/remove";
import lodashFind from "lodash/find";

import { GRIDS_TYPES } from "@/common/constants/grids";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import { getTopicsArray, getTopicKey } from "@/services/events/topics.service";
import { getVarianceScoreColor } from "@/services/grids/grids.service";
import { LABELS } from "@/common/constants/labels";
import {
  hasEssentialsAccess,
  hasPlusCyberAccess,
  hasGrdAccess,
  hasMaritimeAccess,
} from "@/services/user/user.service";
import {
  RISK_RATING_PRIMARIES,
  RISK_RATING_SUB,
} from "@/common/constants/controlRisks/risks";

const EMITTED_ACTIONS = {
  FINISHED: "finished",
  WATCH_ENABLED_CLICK: "watch-enabled-click",
};

export default {
  name: "MonitorSummary",
  components: {
    GridHighlight: () => import("@/components/grids/GridHighlight"),
    RiskRatingPrimary: () =>
      import("@/components/controlRisks/risks/RiskRatingPrimary"),
    RisksPulseRatings: () =>
      import("@/components/controlRisks/risks/RisksPulseRatings"),
    PulseAndRiskDefinitions: () =>
      import("@/components/grids/PulseAndRiskDefinitions"),
  },
  props: {
    type: {
      type: String,
    },
    initialCountSum: {
      type: Number,
    },
    todayScore: {
      type: Number,
    },
    pulseScoreMax: {
      type: Number,
    },
    pulseScoreMin: {
      type: Number,
    },
    pulseScoreYesterday: {
      type: Number,
    },
    pulseDelta: {
      type: Number,
    },
    forecast: {
      type: Array,
    },
    isHistory: {
      type: Boolean,
      default: false,
    },
    trigger: {
      type: Array,
    },
    height: {
      type: Number,
      default: 145,
    },
    location: {
      type: String,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showWatchBtn: {
      type: Boolean,
      default: false,
    },
    watchEnabled: {
      type: Boolean,
      default: false,
    },
    riskRatingsCountry: {
      type: Object,
    },
  },
  data: function () {
    const primaryRiskRatings = Object.keys(
      RISK_RATING_PRIMARIES
    ).map((key) => ({ key, value: RISK_RATING_PRIMARIES[key] }));
    if (!hasPlusCyberAccess()) {
      lodashRemove(primaryRiskRatings, (r) => {
        return r.key === "cyber";
      });
    }
    if (!hasMaritimeAccess()) {
      lodashRemove(primaryRiskRatings, (r) => {
        return r.value === RISK_RATING_PRIMARIES.maritime;
      });
    }
    const hasGrds = hasGrdAccess();

    const risksParams = hasGrds
      ? {
          hidePulse: true,
        }
      : null;

    return {
      watchBtnDisabled: true,
      labels: LABELS,
      hasEssentials: hasEssentialsAccess(),
      primaryRiskRatings: primaryRiskRatings,
      hasGrds: hasGrds,
      risksParams: risksParams,
    };
  },
  computed: {
    styleWatchButton() {
      let value = "none";
      if (this.showWatchBtn && !this.watchEnabled) {
        value = "inline";
      }
      return {
        display: value,
      };
    },
    styleContainer() {
      return {
        height: this.height,
      };
    },
    isHotspots() {
      if (this.type.indexOf(DETAIL_SIDEBAR_TYPES.hotspots) > -1) {
        return true;
      }
      return false;
    },
    isControlRisksRatings() {
      if (
        this.type.indexOf(DETAIL_SIDEBAR_TYPES.controlRisksRatings) > -1 &&
        this.riskRatingsCountry
      ) {
        return true;
      }
      return false;
    },
    selectedLocation() {
      return {
        iso2Code:
          this.riskRatingsCountry &&
          this.riskRatingsCountry.properties &&
          this.riskRatingsCountry.properties.isoCode2
            ? this.riskRatingsCountry.properties.isoCode2
            : null,
      };
    },
    riskRatings() {
      if (lodashGet(this.riskRatingsCountry, "properties.ratings")) {
        const primaryRiskCategoryIds = this.primaryRiskRatings.map((rr) => {
          return rr.value;
        });
        let ratings = [];
        this.riskRatingsCountry.properties.ratings.forEach((rating) => {
          if (
            lodashGet(rating, "riskCategoryId") &&
            primaryRiskCategoryIds.includes(rating.riskCategoryId)
          ) {
            this.$set(ratings, ratings.length, {
              id: rating.riskCategoryId,
              type: rating.riskCategoryName,
              value: rating.rateValue,
              order: this.primaryRiskRatings.findIndex(
                (x) => x.value == rating.riskCategoryId
              ),
            });
          }
        });
        ratings = lodashOrderBy(ratings, ["order"], ["asc"]);
        return ratings;
      }
      return null;
    },
    risksCategory() {
      return this.hasGrds
        ? lodashFind(this.riskRatingsCountry.properties.ratings, [
            "riskCategoryId",
            RISK_RATING_PRIMARIES.security,
          ])
        : null;
    },
    hasStability() {
      if (this.type === DETAIL_SIDEBAR_TYPES.stability && this.todayScore) {
        return true;
      }
      return false;
    },
    hotspotColor() {
      return this.type === "hotspotsCovid" ? "orange" : "red";
    },
    triggers() {
      let topics = [];
      if (this.type === "hotspotsCovid") {
        topics.push("COVID-19");
      }

      const TOPICS = getTopicsArray();
      if (this.trigger && Array.isArray(this.trigger) && this.trigger.length) {
        this.trigger.forEach((trig) => {
          TOPICS.forEach((topic) => {
            const typeName = getTopicKey(topic.apiName);
            if (
              trig === typeName &&
              !topic.supportsVerified &&
              (!topic.parentIds ||
                (topic.parentIds &&
                  this.featureUnlocked(
                    `hotspot-subevent-${trig.replace("_", "-")}`
                  )))
            ) {
              topics.push(topic.singular);
            }
          });
        });
      }
      return topics;
    },
    score() {
      if (this.todayScore) {
        return this.todayScore;
      }
      return null;
    },
    scoreDelta() {
      if (this.pulseDelta) {
        return Math.floor(this.pulseDelta);
      }
      if (this.pulseScoreYesterday && this.score) {
        return Math.floor(this.score - this.pulseScoreYesterday);
      }
      return null;
    },
    minScore() {
      if (typeof this.pulseScoreMin === "number") {
        let min = 0;
        if (typeof this.pulseScoreMin === "number") {
          min =
            this.pulseScoreMin < 1
              ? this.pulseScoreMin
              : Math.floor(this.pulseScoreMin);
        }
        return min;
      }
      return null;
    },
    maxScore() {
      if (typeof this.pulseScoreMax === "number") {
        return Math.floor(this.pulseScoreMax);
      }
      return null;
    },
    colorPulse() {
      if (this.score) {
        const PARAMS = {
          type: GRIDS_TYPES.stability,
          varianceScore: this.score,
        };
        const COLOR = getVarianceScoreColor(PARAMS);
        return COLOR;
      }
      return null;
    },
  },
  methods: {
    handleChartFinished() {
      this.watchBtnDisabled = false;
      this.$emit(EMITTED_ACTIONS.FINISHED, true);
    },
    toggleMonitor() {
      let placeName = undefined;
      if (lodashGet(this, "$refs.locationMunicipalityRef.locationName")) {
        placeName = this.$refs.locationMunicipalityRef.locationName;
      }
      if (lodashGet(this.riskRatingsCountry, "name")) {
        placeName = this.riskRatingsCountry.name;
      }
      this.$emit(EMITTED_ACTIONS.WATCH_ENABLED_CLICK, {
        regionName: placeName,
      });
    },
  },
  watch: {},
};
</script>

<style scoped>
.hyp-header {
  background-color: #191a1a !important;
  text-align: center;
}
.hyp-no-data-text {
  color: gray !important;
}
.hyp-open-watch-btn {
  margin-top: 5px;
  position: relative;
  top: -10px;
  height: 28px !important;
  width: 150px !important;
}
.risk-ratings {
  margin-left: 10px;
}
.ratings-icon-container {
  position: relative;
  top: -2px;
}
</style>
